import {
  Component,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import AwsS3, { AwsS3UploadParameters } from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import axios from 'axios';
import { Subject, takeUntil } from 'rxjs';
import { UserModel } from '../../../../models/user.model';
import { UtilsService } from '../../../../services/utils.service';
import { AppState } from '../../../../state/appstate';

@Component({
  selector: 'app-s3-image-upload',
  standalone: true,
  imports: [
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
  ],
  templateUrl: './s3-image-upload.component.html',
  styleUrl: './s3-image-upload.component.css',
})
export class S3ImageUploadComponent implements OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  private fbAuth: Auth = inject(Auth);
  private utilsService = inject(UtilsService);

  showLabels = input<boolean | undefined>(true);

  label = input<string | undefined>();
  subTitle = input<string | undefined>();
  preSignedUrlEndpoint = input.required<string>();
  cloudFrontDomain = input.required<string>();
  apikey = input.required<string>();

  metadata = input<any | undefined>();

  @Output() uploadStatus = new EventEmitter<{
    success: boolean;
    response?: any;
    error?: any;
  }>();

  user: UserModel | null = null;

  exit$: Subject<void> = new Subject<void>();

  uppy: Uppy = new Uppy({
    autoProceed: false,
    restrictions: {
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });

  dashboardProps = {
    inline: true,
    hideUploadButton: true,
    hideRetryButton: true,
    hidePauseResumeButton: true,
    hideProgressAfterFinish: true,
    showSelectedFiles: true,
    showProgressDetails: true,
    proudlyDisplayPoweredByUppy: false,
    width: '100%',
    height: '130px',
    note: 'Images only (jpg, jpeg, png), up to 5 MB. Only one image is allowed.',
  };

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.uppy.use(AwsS3, {
      limit: 1,
      getUploadParameters: this.getSignedUrl.bind(this),
      shouldUseMultipart: false,
    });

    this.uppy.on('upload-success', (file, response) => {
      // console.log(`Upload success: ${file?.name}`);
      // console.log(`Response: ${JSON.stringify(response, null, 2)}`);
      const bucketPathPattern =
        /^https?:\/\/([^.]+)\.s3\.[^.]+\.amazonaws\.com\/(.*)$/;
      const newUrl = response.uploadURL?.replace(
        bucketPathPattern,
        `https://${this.cloudFrontDomain()}/$2`,
      );
      response.uploadURL = newUrl;
      this.uploadStatus.emit({ success: true, response });
    });

    this.uppy.on('upload-error', (file, error) => {
      console.error(`Upload failed: ${file?.name}`, error);
      this.uploadStatus.emit({ success: false, error });
    });
  }

  ngOnDestroy(): void {
    this.uppy.destroy();
    this.exit$.next();
    this.exit$.complete();
  }

  async getSignedUrl(file: any, options: any): Promise<AwsS3UploadParameters> {
    if (this.user?.uid === null) {
      throw new Error('Unsuccessful request', { cause: 'User not found' });
    }

    const fileExtn: string | undefined = file.name?.split('.')?.pop();
    const token: string | undefined =
      await this.fbAuth.currentUser?.getIdToken();

    if (token === undefined) {
      throw new Error('Unsuccessful request', {
        cause: 'Failed to authenticate user',
      });
    }
    try {
      const response = await axios.get<any>(this.preSignedUrlEndpoint(), {
        params: {
          uid: this.user?.uid,
          name: file.name,
          fileExtension: fileExtn,
          ...this.metadata(),
          aspectRatio: '1',
        },
        headers: {
          'x-api-key': this.apikey(),
          AuthorizationToken: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!(response.status === 200)) {
        throw new Error('Unsuccessful request', { cause: response });
      }

      // Parse the JSON response.
      const data = await response.data;

      return {
        method: 'PUT',
        url: data.url,
        headers: {
          'Content-Type': file.type,
        },
      };
    } catch (error) {
      console.error(`Error getting upload parameters: ${error}`);
      throw error;
    }
  }

  upload() {
    if (this.uppy.getFiles().length === 0) {
      this.uploadStatus.emit({ success: true, error: 'No files' });
    } else {
      this.uppy.upload();
    }
  }
}
